// Hit-effect art in the creature style. Each element has a five-step ramp from the creature
// palette and a few tiny particle sprites (a flame tongue, a droplet, a leaf, a bolt, a rock
// chunk, an ice shard, a wisp, a star) with the same near-black outline the creatures have.
// field.ts decides where and when effects move; this file only says what they look like.
//
// Sprites are written as rows of ramp indices ('1' dark ... '4' highlight, '.' empty). The
// outline is added around every filled pixel when the sprite is first drawn, then the small
// canvas is cached.
import { R } from '../art/rig3d/palette.ts';
import { STYLE } from '../art/rig3d/style.ts';
import type { Element } from '../shared/elements.ts';

type Ramp5 = readonly [string, string, string, string, string];

/** Each element's ramp: outline-dark, shadow, mid, light, highlight. */
export const EL_RAMP: Record<Element, Ramp5> = {
  flame: R.ember,
  tide: R.water,
  grove: R.leaf,
  storm: R.storm,
  stone: ['#2a1e1a', '#54402e', '#86684a', '#b49470', '#e0c8a0'],
  frost: R.ice,
  shade: R.dusk,
  light: R.sunGold,
};

/** Big and small particle sprites per element. */
const PARTICLE: Record<Element, [string[], string[]]> = {
  flame: [['..4.', '.44.', '.343', '3332', '.22.'], ['.4.', '343', '.2.']],
  tide: [['.4.', '434', '332', '.2.'], ['4.', '32']],
  grove: [['..33', '.343', '3432', '22..'], ['.3', '32']],
  storm: [['.44', '44.', '.44', '44.', '4..'], ['4', '4', '3']],
  stone: [['.43.', '4332', '3221'], ['43', '21']],
  frost: [['.4', '43', '32', '2.'], ['.4.', '434', '.4.']],
  shade: [['.32.', '3221', '.11.'], ['3.', '21']],
  light: [['..4..', '..4..', '44444', '..4..', '..4..'], ['.4.', '444', '.4.']],
};

/** An impact star in three growing frames: four long rays, four short ones, the centre opening up. */
const SPARK: string[][] = (() => {
  const frames: string[][] = [];
  const spec = [[0, 3, 2], [2, 5, 3], [4, 7, 5]]; // [inner, outer, diagonal outer]
  for (const [r0, r1, rd] of spec) {
    const n = r1 * 2 + 1, c = r1;
    const rows = Array.from({ length: n }, () => Array(n).fill('.'));
    for (let r = r0; r <= r1; r++) {
      const v = r <= r0 + 1 ? '4' : '3';
      rows[c][c + r] = rows[c][c - r] = rows[c + r][c] = rows[c - r][c] = v;
    }
    for (let r = Math.max(1, r0 - 1); r <= rd - 1; r++) {
      const v = r <= r0 ? '4' : '3';
      rows[c + r][c + r] = rows[c - r][c - r] = rows[c + r][c - r] = rows[c - r][c + r] = v;
    }
    frames.push(rows.map((row) => row.join('')));
  }
  return frames;
})();

const cache = new Map<string, HTMLCanvasElement>();

/**
 * Renders rows of ramp indices into a small canvas (1 px per sprite pixel), padded by a pixel.
 * part 'line' is only the one-pixel outline around the shape, 'fill' only the shape, 'all' both.
 * Drawing every line before any fill lets overlapping particles merge into one outlined cluster.
 */
function bake(key: string, rows: string[], ramp: Ramp5, part: 'all' | 'line' | 'fill' = 'all'): HTMLCanvasElement {
  key += ':' + part;
  let c = cache.get(key);
  if (c) return c;
  const h = rows.length, w = Math.max(...rows.map((r) => r.length));
  c = document.createElement('canvas');
  c.width = w + 2; c.height = h + 2;
  const x = c.getContext('2d')!;
  const filled = (i: number, j: number) => j >= 0 && j < h && i >= 0 && i < w && rows[j][i] !== undefined && rows[j][i] !== '.';
  x.fillStyle = STYLE.outline;
  if (part !== 'fill') for (let j = -1; j <= h; j++) for (let i = -1; i <= w; i++) {
    if (filled(i, j)) continue;
    if (filled(i - 1, j) || filled(i + 1, j) || filled(i, j - 1) || filled(i, j + 1)) x.fillRect(i + 1, j + 1, 1, 1);
  }
  if (part !== 'line') for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    if (!filled(i, j)) continue;
    x.fillStyle = ramp[Number(rows[j][i])];
    x.fillRect(i + 1, j + 1, 1, 1);
  }
  cache.set(key, c);
  return c;
}

/** A particle sprite for an element: big or small; its outline and its fill can be drawn apart. */
export function particleSprite(el: Element, big: boolean, part: 'all' | 'line' | 'fill' = 'all'): HTMLCanvasElement {
  return bake(`p:${el}:${big ? 1 : 0}`, PARTICLE[el][big ? 0 : 1], EL_RAMP[el], part);
}

/** The impact star for an element, frame 0-2. */
export function sparkSprite(el: Element, frame: number): HTMLCanvasElement {
  const f = Math.max(0, Math.min(SPARK.length - 1, frame));
  return bake(`s:${el}:${f}`, SPARK[f], EL_RAMP[el]);
}

/** Draws a baked sprite centred on (x, y) at a whole-number pixel scale. */
export function stamp(g: CanvasRenderingContext2D, c: HTMLCanvasElement, x: number, y: number, scale: number, flip = false) {
  const s = Math.max(1, Math.round(scale));
  const w = c.width * s, h = c.height * s;
  g.drawImage(flip ? mirrored(c) : c, Math.round(x - w / 2), Math.round(y - h / 2), w, h);
}

/** A sprite mirrored left to right, made once (a save/scale/restore per particle was costly without a GPU). */
const mirrors = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
function mirrored(c: HTMLCanvasElement): HTMLCanvasElement {
  let m = mirrors.get(c);
  if (!m) {
    m = document.createElement('canvas'); m.width = c.width; m.height = c.height;
    const x = m.getContext('2d')!;
    x.scale(-1, 1); x.drawImage(c, -c.width, 0);
    mirrors.set(c, m);
  }
  return m;
}
